/**
 * L'app en BAC À SABLE (17/09) : l'aperçu des jeux, ouvert par le CMS.
 *
 * Le mode se décide sur l'URL, au chargement, AVANT tout le reste : le stockage
 * et le transport sont aiguillés dès leur premier appel. En bac à sable :
 *
 * - le stockage vit en mémoire (il n'écrase pas la vraie app du navigateur) ;
 * - aucune requête ne part : le transport est simulé, et refuse ce qu'il ne
 *   connaît pas ;
 * - aucune mesure d'audience (il n'y en a plus nulle part depuis le 08/10).
 *
 * La seule question posée au back est celle du jeton (`auth/preview-token`),
 * pour que la route ne serve rien au public. Détail :
 * `roadmap/16_APERCU_DES_JEUX.md`.
 */

export const SANDBOX_PATH = '/preview'

export const SANDBOX = typeof window !== 'undefined' && window.location.pathname.startsWith(SANDBOX_PATH)

/** L'issue imposée au prochain tour de roue, ou le hasard. `gain:<i>` : l'option n° i. */
export type WheelOutcome = 'random' | 'lost' | 'replay' | `gain:${number}`

/** Ce que le CMS règle de la simulation, à côté de la configuration du jeu. */
export type Simulation = {
    /** Déjà inscrit : le formulaire d'inscription est sauté. */
    registered?: boolean
    wheel?: {
        outcome?: WheelOutcome
        /** Tours de la partie ; à défaut, ceux du jeu. */
        roundsCount?: number
        /** Le lot de consolation annoncé en fin de partie, à qui n'a rien gagné. */
        consolation?: string
    }
}

/** L'état d'une partie simulée. Remis à zéro à chaque configuration reçue. */
export const sandboxState = {
    game: undefined as any,
    simulation: {} as Simulation,
    registered: false,
    /** Les photos déposées (le photobooth en accepte trois). */
    photos: 0,
    roundsLeft: 0,
    won: false,
    /** La partie de quiz individuel : le serveur sert les questions, ici c'est nous. */
    solo: { served: 0, servedAt: 0, right: 0, answered: 0 }
}

export const resetSandbox = (game: any, simulation: Simulation = {}) => {
    sandboxState.game = game
    sandboxState.simulation = simulation
    sandboxState.registered = Boolean(simulation.registered)
    sandboxState.photos = 0
    // Les tours : ceux de la simulation, sinon ceux de la dotation, sinon un (07/10, comme le serveur).
    sandboxState.roundsLeft = simulation.wheel?.roundsCount ?? game?.wheel?.roundsCount ?? 1
    sandboxState.won = false
    sandboxState.solo = { served: 0, servedAt: 0, right: 0, answered: 0 }
}

/** Le nom de l'événement qui relance la partie (bouton fermer, fin de partie). */
export const SANDBOX_RESTART = 'luckysit:sandbox-restart'

/** L'origine du CMS : celle du document qui a ouvert l'iframe, et nulle autre. */
export const parentOrigin = (() => {
    if (!SANDBOX) return undefined
    try {
        return document.referrer ? new URL(document.referrer).origin : undefined
    } catch {
        return undefined
    }
})()

/** Écrire au CMS. Rien ne part vers une autre origine. */
export const tellParent = (message: Record<string, unknown>) => {
    if (parentOrigin && window.parent !== window) window.parent.postMessage(message, parentOrigin)
}

/**
 * Le résultat d'un quiz, calculé par le meneur du CMS et envoyé juste avant la
 * fin de partie : la page de résultat le lit comme elle lirait le back.
 */
export const quizResult = {
    mine: undefined as { rightAnswersCount: number; rank: number; wonGain: null } | undefined,
    ranking: [] as { _id: string; firstName: string; lastName: string; score: number }[]
}
